import React, { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Modal } from "./common";
import Avatar from "./Avatar";
import { useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";

export default function DeductPointsModal({ open, onClose, students = [], presetId = null, onDone }) {
  const { t, pick } = useLang();
  const [studentId, setStudentId] = useState(presetId);
  const [amount, setAmount] = useState(5);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const student = students.find((s) => s.id === studentId);

  const reset = () => { setStudentId(presetId); setAmount(5); setReason(""); setConfirming(false); };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/points/deduct", { student_id: studentId, amount, reason });
      toast.success(t("teacher.deducted_ok", { n: amount }), {
        action: data.tx_id ? {
          label: t("common.undo"),
          onClick: async () => {
            await api.post(`/staff/points/${data.tx_id}/undo`).catch(() => {});
            toast.success(t("teacher.undone_ok"));
            onDone?.();
          },
        } : undefined,
        duration: 8000,
      });
      reset();
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title={t("teacher.deduct_points")} testId="deduct-points-modal">
      {!confirming ? (
        <div className="space-y-4">
          {!presetId && (
            <div>
              <p className="text-sm font-extrabold mb-2">{t("teacher.student_label")}</p>
              <div className="max-h-40 overflow-y-auto space-y-1.5">
                {students.map((s) => (
                  <button key={s.id} data-testid={`deduct-student-${s.username}`} onClick={() => setStudentId(s.id)}
                    className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 border transition-colors ${studentId === s.id ? "bg-[#EF4D9A]/20 border-[#EF4D9A]/60" : "bg-[#0D0B38]/50 border-white/8 hover:border-white/20"}`}>
                    <Avatar src={s.avatar} name={pick(s, "name")} size="xs" />
                    <span className="text-sm font-bold">{pick(s, "name")}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-sm font-extrabold mb-1.5">{t("dash.points_label")}</p>
            <div className="flex items-center gap-2">
              <button data-testid="deduct-minus" onClick={() => setAmount(Math.max(1, amount - 1))} className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Minus className="w-4 h-4" /></button>
              <span data-testid="deduct-amount" className="flex-1 text-center text-2xl font-black text-[#FF8FB1]">-{amount}</span>
              <button data-testid="deduct-plus" onClick={() => setAmount(Math.min(100, amount + 1))} className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
          <div>
            <p className="text-sm font-extrabold mb-1.5">{t("teacher.reason")} *</p>
            <input data-testid="deduct-reason-input" className="input-magic" value={reason} onChange={(e) => setReason(e.target.value)} />
            {!reason && confirming === false && null}
          </div>
          <button data-testid="deduct-preview-btn" disabled={!studentId || !reason.trim()}
            onClick={() => setConfirming(true)} className="btn-magic btn-pink w-full">
            {t("common.confirm")} ←
          </button>
          {!reason.trim() && <p className="text-xs text-[#FF8FB1] font-bold text-center">{t("teacher.reason_required")}</p>}
        </div>
      ) : (
        <div className="space-y-5 text-center">
          <h4 className="text-2xl font-black text-[#FF8FB1]">{t("teacher.confirm_deduct", { n: amount })}</h4>
          <div className="rounded-2xl bg-[#0D0B38]/60 border border-white/10 p-4 space-y-2 text-sm font-bold">
            <p><span className="text-[#A5A0D6]">{t("teacher.student_label")}: </span>{student ? pick(student, "name") : ""}</p>
            <p><span className="text-[#A5A0D6]">{t("teacher.reason")}: </span>{reason}</p>
          </div>
          <p className="font-extrabold text-[#D8D4F2]">{t("common.are_you_sure")}</p>
          <div className="flex gap-3">
            <button data-testid="deduct-cancel-btn" onClick={() => setConfirming(false)} className="btn-magic btn-ghost flex-1">{t("common.cancel")}</button>
            <button data-testid="deduct-confirm-btn" disabled={busy} onClick={submit} className="btn-magic btn-pink flex-1">
              {busy ? t("common.loading") : t("common.confirm")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
